"use client";

// team — the four people, in public.
//
// THE BODY ONLY, read inside the company rail (see AboutBody for why): the
// second night room, paired with about — about says what the thing is, team
// says who is doing it, and they wear the same construction in different hues
// so they read as two halves of one answer.
//
// ⚠️ EVERY WORD ABOUT A PERSON HERE IS THEIR OWN. The names, roles, crafts and
// one-line self-descriptions come from lib/company/founders, which carries the
// team board verbatim — the same array the investor deck renders. Nothing on
// this page is a paraphrase, and nothing about anyone's background is inferred:
// the repo does not hold prior companies, education or locations for any of
// them, and a public page is the last place to guess at those.
//
// THE COPY (2026-09-30) is the team's own, from the notion page "about & team
// copy". The founders stand as they did: static, no tile, no hover.
//
// The face wears the innernet cell, the same way it does on the deck's team
// board and the same way every avatar in the product does (law_cell).

import Link from "next/link";
import { FOUNDERS, TEAM_NAME } from "../../lib/company/founders";
import { SOCIAL_MARKS, SOCIAL_FRAME as SF } from "../../lib/brand/socialMarks";
import { PillButton } from "../ui";
import BrandIcon from "../BrandIcon";
import "./about-figures.css";

/** the house LinkedIn mark — the ONE social registry, the same one the deck and
 *  the site footer draw from, so a founder's door can never drift from ours. */
const LINKEDIN = SOCIAL_MARKS.find((m) => m.name === "linkedin")!;

function LinkedInMark({ size = 20 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.45}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x={SF.x} y={SF.y} width={SF.size} height={SF.size} rx={SF.rx} />
      <g dangerouslySetInnerHTML={{ __html: LINKEDIN.inner }} />
    </svg>
  );
}

export default function TeamBody({ signedIn = false }: { signedIn?: boolean }) {
  return (
    <div className="co-body ab-body">
      {/* the headline the page used to pin — now the first thing under the rail */}
      <h1 className="ab-title os-enter" style={{ ["--i" as string]: 0 }}>
        we call ourselves {TEAM_NAME}.
      </h1>

      <section className="ab-sec">
        <p className="ab-lede">
          there are four of us, everyone insanely in love with what they do and with what the four of
          us do together.
        </p>
        <p>
          we fit, which is rarer than it sounds. product design, economics, operations, whatever else
          it takes. all of it fits together. innernet is what you see as a result.
        </p>
      </section>

      <section className="ab-sec">
        <p className="ab-beat">
          we are small on purpose. <span style={{ color: "var(--ink-3)" }}>the words below are each person&rsquo;s own.</span>
        </p>
      </section>

      <section className="tm-grid" aria-label="the founders">
        {FOUNDERS.map((f, i) => (
          <article key={f.name} className="tm-person os-enter" style={{ ["--i" as string]: i + 2 } as React.CSSProperties}>
            {/* the white cell is the element; the photograph wears the same
                cell inside it, exactly as the team board frames it. */}
            <span className="tm-face">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img className="tm-photo" src={f.photo} alt={f.name} draggable={false} />
            </span>
            <h2 className="tm-name">
              {f.name} <span className="tm-role">{f.role}</span>
            </h2>
            <p className="tm-craft">{f.craft}</p>
            <a
              className="tm-in"
              href={f.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${f.name} on LinkedIn`}
            >
              <LinkedInMark />
            </a>
            <p className="tm-line">{f.line}</p>
          </article>
        ))}
      </section>

      <section className="ab-sec ab-sec--close">
        <div className="gateway-row">
          <PillButton
            as="a"
            href={signedIn ? "/dashboard" : "/signup?from=team"}
            icon={<BrandIcon name="innernet" size={17} className="pill-netti" />}
          >
            {signedIn ? "open your innernet" : "start your innernet"}
          </PillButton>
          <Link className="quiet-link" href="/company/about">what we are building</Link>
          <Link className="quiet-link" href="/company">read the letters</Link>
        </div>
        <p className="ab-close__note">
          anything for all four of us:{" "}
          <a className="quiet-link" href="mailto:connect@innernet.live">connect@innernet.live</a>. the
          founders read it, not a queue.
        </p>
      </section>
    </div>
  );
}
